import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../shared/api';

/**
 * m.2.3 postavke alokacija: osobe (place po osobi), rucni overhead po mjesecu i kategoriji, grupe poslovnica, audit s revertom.
 * API: /api/pl-v23/settings (+ person, override, group, audit/{id}/revert). Pravila provjerava backend (Settings::validate*),
 * ekran samo prikazuje poruku greske. Urednici: pl_cube.alloc.editors.
 */

type Opt = { key: string; label: string; branch?: boolean };
type Share = { pct: number; label?: string } & Record<string, unknown>;
type Person = {
    id: number; person_key: string; employee_id: number | null; oib: string | null; person_name: string; source_company: string;
    valid_from: string; kind: 'operativni' | 'overhead'; overhead_category: string | null;
    sectors: Array<{ sector: string; pct: number }>; mts: Array<{ mt: string; pct: number }>;
    chain_split: Record<string, number> | null; keep_in_source: boolean; note: string | null;
};
type Group = { id: number; name: string; scope: 'company' | 'personal'; owner_user_id: number | null; members: string[] };
type Override = { id: number; ym: string; category: string; sector: string; pct: number | string };
type Audit = {
    id: number; user_email: string; entity: string; entity_id: number | null; action: string;
    old: Record<string, unknown> | null; new: Record<string, unknown> | null; created_at: string;
    reverted_at: string | null; reverts_audit_id: number | null; can_revert: boolean;
};
type Payload = {
    can_edit: boolean; sectors: Opt[]; overhead_categories: Opt[]; chain_companies: string[]; chain_source_companies: string[];
    max_mts: number; branches: string[]; mt_options: Opt[];
    employees: Array<{ id: number; name: string; oib: string | null; company: string | null }>;
    persons: Person[]; overrides: Override[]; groups: Group[]; audit: Audit[];
};

const INK = '#1a202c';
const lbl: React.CSSProperties = { display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.3rem', color: '#555' };
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid ${INK}`, fontSize: 12, whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #ddd', fontSize: 13, verticalAlign: 'top' };
const inp: React.CSSProperties = { padding: '5px 7px', border: '1px solid #999', fontSize: 13, fontFamily: 'inherit' };
const btn: React.CSSProperties = { padding: '5px 12px', border: `1px solid ${INK}`, background: '#fff', color: INK, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const btnSolid: React.CSSProperties = { ...btn, background: INK, color: '#fff' };

const TABS = [
    { key: 'osobe', label: 'Osobe' },
    { key: 'overhead', label: 'Rucni overhead' },
    { key: 'grupe', label: 'Grupe poslovnica' },
    { key: 'audit', label: 'Audit' },
] as const;
type Tab = typeof TABS[number]['key'];

const sum = (rows: Array<{ pct: number | string }>) => rows.reduce((a, r) => a + (Number(r.pct) || 0), 0);
const fmtShares = (rows: Array<Record<string, unknown>> | null | undefined, key: string, labels: Map<string, string>) =>
    (rows ?? []).map((r) => `${labels.get(String(r[key])) ?? String(r[key])} ${Number(r.pct)} %`).join(', ');

function nowYm(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Retci s postotkom: kljuc iz popisa + postotak, zbroj se prikazuje uz upozorenje ako nije 100. */
function ShareRows({ rows, onChange, options, keyName, max, disabled }: {
    rows: Share[]; onChange: (r: Share[]) => void; options: Opt[]; keyName: string; max?: number; disabled?: boolean;
}) {
    const total = sum(rows);
    return (
        <div>
            {rows.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                    <select style={{ ...inp, minWidth: 200 }} disabled={disabled} value={String(r[keyName] ?? '')}
                        onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, [keyName]: e.target.value } : x)))}>
                        <option value="">-</option>
                        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                    </select>
                    <input style={{ ...inp, width: 70, textAlign: 'right' }} type="number" min={0} max={100} step={0.1} disabled={disabled}
                        value={r.pct} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, pct: Number(e.target.value) } : x)))} />
                    <span style={{ alignSelf: 'center' }}>%</span>
                    {!disabled ? <button type="button" style={btn} onClick={() => onChange(rows.filter((_, j) => j !== i))}>Makni</button> : null}
                </div>
            ))}
            {!disabled && (max === undefined || rows.length < max) ? (
                <button type="button" style={btn} onClick={() => onChange([...rows, { [keyName]: '', pct: rows.length === 0 ? 100 : Math.max(0, 100 - total) }])}>Dodaj</button>
            ) : null}
            {rows.length > 0 ? (
                <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 700, color: Math.abs(total - 100) > 0.01 ? '#c62828' : INK }}>
                    Zbroj {Math.round(total * 1000) / 1000} %{Math.abs(total - 100) > 0.01 ? ' (mora biti 100)' : ''}
                </span>
            ) : null}
        </div>
    );
}

type PersonDraft = Omit<Person, 'id' | 'person_key' | 'employee_id'> & { employee_id: number | '' };

function emptyPerson(p: Payload): PersonDraft {
    return {
        employee_id: '', oib: '', person_name: '', source_company: '', valid_from: nowYm(), kind: 'operativni', overhead_category: null,
        sectors: [], mts: [], chain_split: Object.fromEntries(p.chain_companies.map((c) => [c, 0])), keep_in_source: false, note: '',
    };
}

export function PnlKockaSettings({ token }: { token: string | null }) {
    const [data, setData] = useState<Payload | null>(null);
    const [tab, setTab] = useState<Tab>('osobe');
    const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState<PersonDraft | null>(null);
    const [ovYm, setOvYm] = useState(nowYm());
    const [ovCat, setOvCat] = useState('');
    const [ovRows, setOvRows] = useState<Share[]>([]);
    const [grp, setGrp] = useState<{ id: number | null; name: string; scope: 'company' | 'personal'; members: string[] } | null>(null);

    const load = useCallback(() => {
        if (!token) return;
        api<Payload>('/pl-v23/settings', token)
            .then((d) => { setData(d); setOvCat((c) => c || (d.overhead_categories.find((o) => o.key !== 'poslovnica')?.key ?? '')); })
            .catch((e) => setMsg({ ok: false, text: e instanceof Error ? e.message : 'Postavke se nisu ucitale.' }));
    }, [token]);
    useEffect(load, [load]);

    const call = async (path: string, method: string, body?: unknown, okText = 'Spremljeno.') => {
        setBusy(true);
        try {
            await api(path, token, { method, body: body === undefined ? undefined : JSON.stringify(body) });
            setMsg({ ok: true, text: okText });
            load();
            return true;
        } catch (e) {
            setMsg({ ok: false, text: e instanceof Error ? e.message : 'Greska.' });
            return false;
        } finally {
            setBusy(false);
        }
    };

    const labels = useMemo(() => {
        const m = new Map<string, string>();
        data?.sectors.forEach((s) => m.set(s.key, s.label));
        data?.overhead_categories.forEach((s) => m.set(s.key, s.label));
        data?.mt_options.forEach((s) => m.set(s.key, s.label));
        return m;
    }, [data]);

    // Rucni overhead: kad se promijeni mjesec ili kategorija, ucitaj postojece retke.
    useEffect(() => {
        if (!data) return;
        setOvRows(data.overrides.filter((o) => o.ym === ovYm && o.category === ovCat).map((o) => ({ sector: o.sector, pct: Number(o.pct) })));
    }, [data, ovYm, ovCat]);

    if (!data) {
        return <div style={{ padding: 16 }}>{msg ? msg.text : 'Ucitavam postavke...'}</div>;
    }
    const ro = !data.can_edit;
    const sectorOpts = data.sectors;
    const ohCats = data.overhead_categories.filter((o) => o.key !== 'poslovnica');

    const personEditor = draft ? (() => {
        const d = draft;
        const set = (patch: Partial<PersonDraft>) => setDraft({ ...d, ...patch });
        const inChain = data.chain_source_companies.includes(d.source_company.toUpperCase()) && !d.keep_in_source;
        const mtOpts = d.kind === 'operativni' ? data.mt_options.filter((o) => o.branch) : data.mt_options;
        return (
            <div style={{ border: `1px solid ${INK}`, padding: 14, margin: '12px 0', background: '#fafafa' }}>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div>
                        <label style={lbl}>Djelatnik (HR)</label>
                        <select style={{ ...inp, minWidth: 240 }} value={d.employee_id} onChange={(e) => {
                            const id = e.target.value === '' ? '' : Number(e.target.value);
                            const emp = data.employees.find((x) => x.id === id);
                            set({ employee_id: id, ...(emp ? { person_name: emp.name, oib: emp.oib ?? d.oib, source_company: emp.company ?? d.source_company } : {}) });
                        }}>
                            <option value="">- nije u HR-u, upisi OIB -</option>
                            {data.employees.map((x) => <option key={x.id} value={x.id}>{x.name}{x.company ? ` (${x.company})` : ''}</option>)}
                        </select>
                    </div>
                    <div><label style={lbl}>Ime</label><input style={inp} value={d.person_name} onChange={(e) => set({ person_name: e.target.value })} /></div>
                    <div><label style={lbl}>OIB</label><input style={{ ...inp, width: 120 }} value={d.oib ?? ''} onChange={(e) => set({ oib: e.target.value })} /></div>
                    <div><label style={lbl}>Poslodavac</label><input style={{ ...inp, width: 150 }} value={d.source_company} onChange={(e) => set({ source_company: e.target.value.toUpperCase() })} placeholder="ADO, ADMS, MONESTIQ..." /></div>
                    <div><label style={lbl}>Vrijedi od</label><input style={inp} type="month" value={d.valid_from} onChange={(e) => set({ valid_from: e.target.value })} /></div>
                </div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div>
                        <label style={lbl}>Vrsta</label>
                        <select style={inp} value={d.kind} onChange={(e) => set({ kind: e.target.value as PersonDraft['kind'], mts: [] })}>
                            <option value="operativni">Operativni (poslovnica)</option>
                            <option value="overhead">Overhead</option>
                        </select>
                    </div>
                    {d.kind === 'overhead' ? (
                        <div>
                            <label style={lbl}>Kategorija overheada</label>
                            <select style={inp} value={d.overhead_category ?? ''} onChange={(e) => set({ overhead_category: e.target.value || null })}>
                                <option value="">-</option>
                                {ohCats.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                            </select>
                        </div>
                    ) : null}
                    <label style={{ alignSelf: 'flex-end', fontSize: 13 }}>
                        <input type="checkbox" checked={d.keep_in_source} onChange={(e) => set({ keep_in_source: e.target.checked })} /> Ostaje trosak poslodavca (ne prelazi u lanac)
                    </label>
                </div>
                <div style={{ display: 'flex', gap: 30, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div><label style={lbl}>Sektori</label><ShareRows rows={d.sectors} onChange={(r) => set({ sectors: r as PersonDraft['sectors'] })} options={sectorOpts} keyName="sector" /></div>
                    <div><label style={lbl}>Mjesto troska (najvise {data.max_mts})</label><ShareRows rows={d.mts} onChange={(r) => set({ mts: r as PersonDraft['mts'] })} options={mtOpts} keyName="mt" max={data.max_mts} /></div>
                    {inChain ? (
                        <div>
                            <label style={lbl}>Prijelaz u lanac</label>
                            {data.chain_companies.map((c) => (
                                <div key={c} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
                                    <span style={{ width: 60 }}>{c}</span>
                                    <input style={{ ...inp, width: 70, textAlign: 'right' }} type="number" min={0} max={100} value={d.chain_split?.[c] ?? 0}
                                        onChange={(e) => set({ chain_split: { ...(d.chain_split ?? {}), [c]: Number(e.target.value) } })} /> %
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div style={{ marginBottom: 10 }}><label style={lbl}>Napomena</label><input style={{ ...inp, width: '100%', maxWidth: 600 }} value={d.note ?? ''} onChange={(e) => set({ note: e.target.value })} /></div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" style={btnSolid} disabled={busy} onClick={async () => {
                        if (await call('/pl-v23/settings/person', 'POST', { ...d, employee_id: d.employee_id === '' ? null : d.employee_id })) setDraft(null);
                    }}>SPREMI</button>
                    <button type="button" style={btn} onClick={() => setDraft(null)}>Odustani</button>
                    <span style={{ fontSize: 12, color: '#555', alignSelf: 'center' }}>Ista osoba i isti mjesec mijenja postojeci zapis; novi mjesec otvara novu verziju.</span>
                </div>
            </div>
        );
    })() : null;

    return (
        <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <div className="bi-seg" role="tablist">
                    {TABS.map((t) => <button key={t.key} type="button" className={'bi-seg-btn' + (tab === t.key ? ' is-active' : '')} onClick={() => setTab(t.key)}>{t.label}</button>)}
                </div>
                {ro ? <span style={{ fontSize: 12, color: '#555' }}>Samo pregled. Postavke mijenjaju Vedran, Andelka, Tonci, Zic, Loris i Tabak.</span> : null}
                {msg ? <span style={{ fontSize: 13, fontWeight: 700, color: msg.ok ? INK : '#c62828' }}>{msg.text}</span> : null}
            </div>

            {tab === 'osobe' ? (
                <div>
                    <p style={{ fontSize: 13, margin: '0 0 8px' }}>
                        Djelatnik bez postavke je trosak svoje poslovnice. Place ADG i Monestiq prelaze u ADO i ADMS po postotku lanca, osim oznacenih da ostaju.
                        Place po osobi jos nisu u izracunu kocke (nema izvora troska po osobi).
                    </p>
                    {!ro && !draft ? <button type="button" style={btnSolid} onClick={() => setDraft(emptyPerson(data))}>NOVA OSOBA</button> : null}
                    {personEditor}
                    <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 10 }}>
                        <thead><tr>
                            <th style={th}>Osoba</th><th style={th}>Poslodavac</th><th style={th}>Od</th><th style={th}>Vrsta</th>
                            <th style={th}>Sektori</th><th style={th}>MT</th><th style={th}>Lanac</th><th style={th}></th>
                        </tr></thead>
                        <tbody>
                            {data.persons.length === 0 ? <tr><td style={td} colSpan={8}>Nema postavki.</td></tr> : null}
                            {data.persons.map((p) => (
                                <tr key={p.id}>
                                    <td style={td}>{p.person_name}<div style={{ fontSize: 11, color: '#777' }}>{p.person_key}</div></td>
                                    <td style={td}>{p.source_company}</td>
                                    <td style={td}>{p.valid_from}</td>
                                    <td style={td}>{p.kind === 'overhead' ? `Overhead: ${labels.get(p.overhead_category ?? '') ?? p.overhead_category}` : 'Operativni'}</td>
                                    <td style={td}>{fmtShares(p.sectors, 'sector', labels)}</td>
                                    <td style={td}>{fmtShares(p.mts, 'mt', labels)}</td>
                                    <td style={td}>{p.keep_in_source ? 'ostaje' : p.chain_split ? Object.entries(p.chain_split).map(([c, v]) => `${c} ${v} %`).join(', ') : '-'}</td>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                        {!ro ? (
                                            <>
                                                <button type="button" style={btn} onClick={() => setDraft({
                                                    employee_id: p.employee_id ?? '', oib: p.oib, person_name: p.person_name, source_company: p.source_company,
                                                    valid_from: p.valid_from, kind: p.kind, overhead_category: p.overhead_category, sectors: p.sectors, mts: p.mts,
                                                    chain_split: p.chain_split ?? Object.fromEntries(data.chain_companies.map((c) => [c, 0])), keep_in_source: p.keep_in_source, note: p.note,
                                                })}>Uredi</button>{' '}
                                                <button type="button" style={btn} disabled={busy} onClick={() => {
                                                    if (window.confirm(`Obrisati postavku ${p.person_name} od ${p.valid_from}?`)) void call(`/pl-v23/settings/person/${p.id}`, 'DELETE', undefined, 'Obrisano.');
                                                }}>Obrisi</button>
                                            </>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {tab === 'overhead' ? (
                <div>
                    <p style={{ fontSize: 13, margin: '0 0 8px' }}>
                        Bez rucne postavke overhead se dijeli po ekonomskom RUC-u sektora. Rucna postavka vrijedi za jedan mjesec i jednu kategoriju;
                        prazan popis brise postavku i vraca raspodjelu po RUC-u. Trosak poslovnice uvijek ide po RUC-u svoje poslovnice.
                    </p>
                    <div style={{ display: 'flex', gap: 14, marginBottom: 10 }}>
                        <div><label style={lbl}>Mjesec</label><input style={inp} type="month" value={ovYm} onChange={(e) => setOvYm(e.target.value)} /></div>
                        <div>
                            <label style={lbl}>Kategorija</label>
                            <select style={inp} value={ovCat} onChange={(e) => setOvCat(e.target.value)}>
                                {ohCats.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <ShareRows rows={ovRows} onChange={setOvRows} options={sectorOpts} keyName="sector" disabled={ro} />
                    {!ro ? (
                        <div style={{ marginTop: 8 }}>
                            <button type="button" style={btnSolid} disabled={busy} onClick={() => void call('/pl-v23/settings/override', 'PUT', { ym: ovYm, category: ovCat, rows: ovRows },
                                ovRows.length === 0 ? 'Rucna postavka obrisana, vrijedi RUC.' : 'Spremljeno.')}>SPREMI</button>
                        </div>
                    ) : null}
                    <h3 style={{ fontSize: 14, margin: '18px 0 6px' }}>Sve rucne postavke</h3>
                    <table style={{ borderCollapse: 'collapse' }}>
                        <thead><tr><th style={th}>Mjesec</th><th style={th}>Kategorija</th><th style={th}>Raspodjela</th></tr></thead>
                        <tbody>
                            {data.overrides.length === 0 ? <tr><td style={td} colSpan={3}>Nema. Sve ide po RUC-u.</td></tr> : null}
                            {Object.entries(data.overrides.reduce<Record<string, Override[]>>((a, o) => { (a[`${o.ym}|${o.category}`] ??= []).push(o); return a; }, {})).map(([k, rows]) => (
                                <tr key={k} style={{ cursor: 'pointer' }} onClick={() => { setOvYm(rows[0].ym); setOvCat(rows[0].category); }}>
                                    <td style={td}>{rows[0].ym}</td>
                                    <td style={td}>{labels.get(rows[0].category) ?? rows[0].category}</td>
                                    <td style={td}>{rows.map((r) => `${labels.get(r.sector) ?? r.sector} ${Number(r.pct)} %`).join(', ')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {tab === 'grupe' ? (
                <div>
                    <p style={{ fontSize: 13, margin: '0 0 8px' }}>Grupe tvrtke vide svi i mijenjaju urednici. Osobne grupe vidi i mijenja samo onaj tko ih je napravio.</p>
                    {!grp ? <button type="button" style={btnSolid} onClick={() => setGrp({ id: null, name: '', scope: ro ? 'personal' : 'company', members: [] })}>NOVA GRUPA</button> : (
                        <div style={{ border: `1px solid ${INK}`, padding: 14, margin: '12px 0', background: '#fafafa' }}>
                            <div style={{ display: 'flex', gap: 14, marginBottom: 10 }}>
                                <div><label style={lbl}>Naziv</label><input style={inp} value={grp.name} onChange={(e) => setGrp({ ...grp, name: e.target.value })} /></div>
                                <div>
                                    <label style={lbl}>Opseg</label>
                                    <select style={inp} value={grp.scope} onChange={(e) => setGrp({ ...grp, scope: e.target.value as 'company' | 'personal' })}>
                                        {!ro ? <option value="company">Tvrtka</option> : null}
                                        <option value="personal">Osobna</option>
                                    </select>
                                </div>
                            </div>
                            <label style={lbl}>Poslovnice ({grp.members.length})</label>
                            <div style={{ columns: 4, fontSize: 13, marginBottom: 10 }}>
                                {data.branches.map((b) => (
                                    <label key={b} style={{ display: 'block' }}>
                                        <input type="checkbox" checked={grp.members.includes(b)}
                                            onChange={(e) => setGrp({ ...grp, members: e.target.checked ? [...grp.members, b] : grp.members.filter((m) => m !== b) })} /> {b}
                                    </label>
                                ))}
                            </div>
                            <button type="button" style={btnSolid} disabled={busy} onClick={async () => { if (await call('/pl-v23/settings/group', 'POST', grp)) setGrp(null); }}>SPREMI</button>{' '}
                            <button type="button" style={btn} onClick={() => setGrp(null)}>Odustani</button>
                        </div>
                    )}
                    <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 10 }}>
                        <thead><tr><th style={th}>Grupa</th><th style={th}>Opseg</th><th style={th}>Poslovnice</th><th style={th}></th></tr></thead>
                        <tbody>
                            {data.groups.length === 0 ? <tr><td style={td} colSpan={4}>Nema grupa.</td></tr> : null}
                            {data.groups.map((g) => {
                                const editable = g.scope === 'personal' || !ro;
                                return (
                                    <tr key={g.id}>
                                        <td style={td}>{g.name}</td>
                                        <td style={td}>{g.scope === 'company' ? 'Tvrtka' : 'Osobna'}</td>
                                        <td style={td}>{g.members.join(', ')}</td>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                            {editable ? (
                                                <>
                                                    <button type="button" style={btn} onClick={() => setGrp({ id: g.id, name: g.name, scope: g.scope, members: g.members })}>Uredi</button>{' '}
                                                    <button type="button" style={btn} disabled={busy} onClick={() => {
                                                        if (window.confirm(`Obrisati grupu ${g.name}?`)) void call(`/pl-v23/settings/group/${g.id}`, 'DELETE', undefined, 'Obrisano.');
                                                    }}>Obrisi</button>
                                                </>
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {tab === 'audit' ? (
                <div>
                    <p style={{ fontSize: 13, margin: '0 0 8px' }}>Zadnjih 100 promjena. Vratiti se mogu zadnjih 30.</p>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead><tr><th style={th}>#</th><th style={th}>Vrijeme</th><th style={th}>Tko</th><th style={th}>Sto</th><th style={th}>Akcija</th><th style={th}>Prije</th><th style={th}>Poslije</th><th style={th}></th></tr></thead>
                        <tbody>
                            {data.audit.length === 0 ? <tr><td style={td} colSpan={8}>Nema promjena.</td></tr> : null}
                            {data.audit.map((a) => (
                                <tr key={a.id} style={{ opacity: a.reverted_at ? 0.5 : 1 }}>
                                    <td style={td}>{a.id}</td>
                                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{String(a.created_at).slice(0, 16).replace('T', ' ')}</td>
                                    <td style={td}>{a.user_email}</td>
                                    <td style={td}>{a.entity.replace('bi_pl23_', '')} {a.entity_id ?? ''}</td>
                                    <td style={td}>{a.action}{a.reverts_audit_id ? ` (#${a.reverts_audit_id})` : ''}{a.reverted_at ? ', vraceno' : ''}</td>
                                    <td style={{ ...td, fontSize: 11, maxWidth: 280, wordBreak: 'break-word' }}>{a.old ? JSON.stringify(a.old) : '-'}</td>
                                    <td style={{ ...td, fontSize: 11, maxWidth: 280, wordBreak: 'break-word' }}>{a.new ? JSON.stringify(a.new) : '-'}</td>
                                    <td style={td}>
                                        {a.can_revert ? <button type="button" style={btn} disabled={busy} onClick={() => {
                                            if (window.confirm(`Vratiti promjenu #${a.id}?`)) void call(`/pl-v23/settings/audit/${a.id}/revert`, 'POST', undefined, 'Vraceno.');
                                        }}>Vrati</button> : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}
        </div>
    );
}
